import { useEffect, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { API_BASE_URL, apiClient } from '../api/client';

/**
 * Live updates (Server-Sent Events from `GET /events`). One connection per
 * signed-in tab; each event is just a "this changed" signal, so we refetch
 * the affected React Query data through the normal API - nothing is pushed
 * that the user couldn't already fetch.
 *
 * While connected, the screens' safety-net polling relaxes to
 * `LIVE_FALLBACK_POLL_MS` (see `pollInterval`); if the stream drops it
 * reconnects with backoff and polling goes back to its normal pace until
 * it's live again.
 */

export const LIVE_FALLBACK_POLL_MS = 60_000;

let connected = false;
const listeners = new Set<() => void>();

function setConnected(next: boolean): void {
  if (connected === next) return;
  connected = next;
  for (const l of listeners) l();
}

export function isLiveConnected(): boolean {
  return connected;
}

export function useLiveConnected(): boolean {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => connected,
    () => false,
  );
}

/** `refetchInterval` helper: the normal pace when offline, a slow safety net when live. */
export function pollInterval(normalMs: number): () => number {
  return () => (connected ? Math.max(normalMs, LIVE_FALLBACK_POLL_MS) : normalMs);
}

function parse(data: string): Record<string, string> {
  try {
    return JSON.parse(data) as Record<string, string>;
  } catch {
    return {};
  }
}

/** What each signal refreshes. Exported for tests. */
export function applyLiveEvent(
  qc: QueryClient,
  type: string,
  data: Record<string, string>,
  refreshDashboard: () => void,
): void {
  switch (type) {
    case 'conversation.changed':
      void qc.invalidateQueries({ queryKey: ['conversations', 'list'] });
      if (data.conversationId) {
        void qc.invalidateQueries({ queryKey: ['conversations', 'messages', data.conversationId] });
        void qc.invalidateQueries({ queryKey: ['conversations', 'detail', data.conversationId] });
      } else {
        // A delivery/read receipt - refresh whichever chat is open.
        void qc.invalidateQueries({ queryKey: ['conversations', 'messages'] });
      }
      refreshDashboard();
      break;
    case 'notification.created':
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      // New leads, orders, appointments... all notify - the dashboard follows.
      refreshDashboard();
      break;
    case 'job.changed':
      if (data.kind === 'catalog_import') {
        void qc.invalidateQueries({ queryKey: ['products-services', 'ai-import-jobs'] });
      } else {
        void qc.invalidateQueries({ queryKey: ['quotations', 'generation-jobs'] });
      }
      break;
    default:
      break;
  }
}

/** Mount once in the signed-in app shell. */
export function useLiveUpdates(enabled: boolean): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    let stopped = false;
    let source: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let dashboardTimer: ReturnType<typeof setTimeout> | undefined;
    let retryMs = 2_000;

    // Many signals in a burst (a busy inbox) -> one dashboard refetch.
    const refreshDashboard = () => {
      if (dashboardTimer) return;
      dashboardTimer = setTimeout(() => {
        dashboardTimer = undefined;
        void qc.invalidateQueries({ queryKey: ['reports', 'dashboard'] });
      }, 3_000);
    };

    const scheduleReconnect = (delay = retryMs) => {
      setConnected(false);
      source?.close();
      source = null;
      if (stopped) return;
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => void connect(), delay);
      retryMs = Math.min(retryMs * 2, 60_000);
    };

    async function connect() {
      try {
        const { ticket } = await apiClient.post<{ ticket: string }>('/events/ticket');
        if (stopped) return;
        const es = new EventSource(`${API_BASE_URL}/events?ticket=${encodeURIComponent(ticket)}`);
        source = es;
        es.addEventListener('ready', () => {
          retryMs = 2_000;
          setConnected(true);
          // Catch up on anything that changed while we weren't listening.
          void qc.invalidateQueries({ queryKey: ['conversations'] });
          void qc.invalidateQueries({ queryKey: ['notifications'] });
        });
        for (const type of ['conversation.changed', 'notification.created', 'job.changed']) {
          es.addEventListener(type, (e) =>
            applyLiveEvent(qc, type, parse((e as MessageEvent<string>).data), refreshDashboard),
          );
        }
        // Server recycles streams every 30 min: reconnect right away with a fresh ticket.
        es.addEventListener('reconnect', () => scheduleReconnect(0));
        // EventSource would retry with the same (soon expired) ticket - take over instead.
        es.onerror = () => scheduleReconnect();
      } catch {
        scheduleReconnect();
      }
    }

    void connect();
    return () => {
      stopped = true;
      clearTimeout(retryTimer);
      clearTimeout(dashboardTimer);
      source?.close();
      setConnected(false);
    };
  }, [enabled, qc]);
}
